import { box, button, checkbox, column, field, row, select, styled, text, textarea, } from '@tiendanube/nube-sdk-ui';
import { DATE_PLACEHOLDER } from './calendar.js';
import { dateControlNodes } from './datepicker.js';
import { fieldIcon } from './icons.js';
import { prefixGutter, prefixLeft } from './prefix.js';
import { codInputCss, fieldErrorCss, inputPaddingWithGutter, codTextareaCss, inputWrapCss, } from './theme.js';
/**
 * Field types whose current value is painted by US, not by an uncontrolled host control.
 *
 * 🚨 A change to one of these MUST trigger a repaint. `FormSession.onFieldChange` deliberately
 * skips repainting for ordinary inputs — `nube.render()` rebuilds the whole modal and would tear
 * the focused input out of the DOM — and that is safe only because the host owns those controls'
 * displayed value. A `single_choice` marker is a `box` we draw from state, so without a repaint the
 * dot simply never moves.
 *
 * The symptom this fixes: picking a second option did nothing until the first was un-ticked. The
 * repaint condition keyed on the empty↔non-empty transition, so `'' → 'A'` and `'A' → ''` both
 * repainted while `'A' → 'B'` — the only case that matters for a radio group — did not.
 */
export function drawsOwnValue(type) {
    // `date` joined this list when the field became a picker: its control is a `button` (only
    // `button` and `link` take an `onClick`), so the visible date is a `text` node painted from
    // state, exactly like a radio marker. Nothing moves it except a re-render.
    return type === 'single_choice' || type === 'date';
}
/**
 * `NubeSDK` has no radio component, so `single_choice` draws its own marker — a bordered circle
 * with an inner dot, exactly like the shipping-rate options (`.cod-radio` / `.cod-radio-on::after`).
 *
 * 🚨 **It must NOT be built from `checkbox()`.** That was the first port, and the state was right —
 * `onFieldChange(f.key, opt)` can only ever store ONE value — while the screen showed **every
 * option ticked at once**. A native checkbox is uncontrolled: the host paints the DOM element,
 * the browser owns its `checked`, and passing `checked: false` on a re-render does not clear a box
 * the buyer already ticked. This is the documented trap in `MIGRATION.md` §4.11 — a control's
 * displayed value is not evidence about `FormState` — and here it ran the other way: state correct,
 * display wrong, which to a buyer is simply a broken radio group.
 *
 * Drawing the marker ourselves makes the rendered output a pure function of state, so it cannot
 * drift. `Form.Radio` exists but only inside `Form.Root`, the FormData POST rejected in
 * `MIGRATION.md` §12.0.
 */
function radioGroup(f, o) {
    const current = o.state.values[f.key] ?? '';
    const pick = o.handlers?.onFieldChange;
    return column({
        style: o.styles.radioGroup,
        children: [
            labelNode(f, o),
            ...f.options.map((opt, i) => {
                const selected = current === opt;
                return button({
                    // 🚨 The key carries BOTH the option index and whether it is chosen, and it needs both.
                    //
                    //  - index: the rows differ only in their children and a closure written at one source
                    //    position, neither of which reaches the SDK's props hash, so without it they collide
                    //    on one `__internalId` and every click lands on the same option.
                    //  - selected: without it the row's id is IDENTICAL before and after being picked — the
                    //    marker's `style` is the same object either way and its dot lives in `children`,
                    //    which the serializer drops. The host then sees no prop change, never repaints the
                    //    row, and the selection appears frozen: measured as "click the second option and
                    //    nothing happens until you un-tick the first".
                    //
                    // The shipping cards get away with an id-stable key because their `style` genuinely
                    // differs when selected (`optionSelected` has its own border), which moves the hash on
                    // its own. These rows have no such difference, so the key has to supply it.
                    key: `${f.key}-opt-${i}-${selected ? 'on' : 'off'}`,
                    style: o.styles.radioOption,
                    // Re-picking the selected option clears it, so a non-required group can be emptied.
                    ...(pick ? { onClick: () => pick(f.key, selected ? '' : opt) } : {}),
                    children: [
                        box({
                            key: 'marker',
                            style: selected ? o.styles.radioOn : o.styles.radio,
                            children: selected ? [box({ style: o.styles.radioDot, children: [] })] : [],
                        }),
                        text({ key: 'label', style: o.styles.radioOptionLabel, children: [opt] }),
                    ],
                });
            }),
        ],
    });
}
/**
 * The label above an input.
 *
 * The required asterisk is a SEPARATE node so it can be red (`.cod-required { color: #d33 }`).
 * Concatenating it into the label string made it inherit the label's colour and render black.
 */
function labelNode(f, o) {
    const label = text({ style: o.styles.field.label, children: [f.label] });
    if (!f.required)
        return label;
    return row({
        style: { alignItems: 'baseline', ...o.styles.field.label, marginBottom: '6px' },
        children: [
            text({ style: { ...o.styles.field.label, marginBottom: '0' }, children: [f.label] }),
            text({ style: o.styles.required, children: ['*'] }),
        ],
    });
}
/**
 * Inline error text under a field, or nothing when it is valid.
 *
 * Through `styled()` as well as the style prop. The gap QA reported was the host wrapper's theme
 * margin (see `nativeContainer`), not this — but `text()` is a paragraph, and a theme that writes
 * `p{margin:… !important}` would beat the inline copy. See `fieldErrorCss`.
 */
function errorNode(f, o) {
    const message = o.state.errors[f.key];
    if (!message)
        return [];
    const StyledError = styled(text) `
    ${fieldErrorCss()}
  `;
    return [StyledError({ style: o.styles.fieldError, children: [message] })];
}
/**
 * Build one input field.
 *
 * Returns a `box` per field so the caller can render `config.fields` in order without knowing
 * which branch a type took — the same contract `renderField` had.
 */
export function fieldNode(f, o) {
    const value = o.state.values[f.key] ?? '';
    /**
     * 🚨 **The input's style must NOT depend on `state.errors`, and this is load-bearing.**
     *
     * There is deliberately no `invalid` variable here any more: nothing about the control may vary
     * with validation, so having one to hand is an invitation to reintroduce the bug below.
     *
     * Reported from a live SDK store: typing fewer characters than a field's `min_length` (or more
     * than its `max_length`) and submitting **wiped what the buyer had typed**. The chain, confirmed
     * by comparing built trees — the failing field's `__internalId` changes and no other field's
     * does:
     *
     *   error style on `style.input` → different props → different `__internalId` → the host treats
     *   it as a new component and remounts it → and these inputs are deliberately UNCONTROLLED
     *   (no `value` prop, see below), so the remounted one comes back **empty**.
     *
     * Every escape route is closed: passing `value` to survive the remount makes the props change on
     * every keystroke, which remounts the FOCUSED input and breaks typing — the exact bug
     * `identity.ts` was written to avoid. Moving the border to the wrapper does not help either:
     * `identity.ts` records that an ancestor's id changing remounts the input too.
     *
     * So the error is signalled by the message under the field and nothing on the control. That is
     * also what the LEGACY bundle has always done — `setFieldError` (`form-renderer/src/form.ts`)
     * writes the message text and never touches the input — so removing the border **converges** the
     * two bundles rather than costing anything. `fieldInputError` is kept in the theme, unused, for
     * whenever the host gains a way to repaint a control without recreating it.
     */
    const inputStyle = o.styles.field.input;
    const onChange = (v) => o.handlers?.onFieldChange?.(f.key, v);
    // Omit handler props ENTIRELY when there are none, rather than attaching a closure that calls
    // `undefined?.()`. Two reasons beyond tidiness: the admin walker (Phase 9) decides whether to
    // wire a React handler by looking for one, and a genuinely handler-free tree is what makes
    // "preview mode is inert" a property you can assert instead of a convention you trust.
    const wired = Boolean(o.handlers?.onFieldChange);
    const changeProp = (handler) => wired ? { onChange: handler } : {};
    // 1. Checkbox — inline label, no separate top label (form.ts:102-111).
    if (f.type === 'checkbox') {
        return column({
            style: o.styles.field.container,
            children: [
                row({
                    style: o.styles.consentRow,
                    children: [
                        checkbox({
                            name: f.key,
                            // `checkbox`'s own label is a plain string and cannot contain a coloured span, so a
                            // required marker is appended beside it rather than inside it.
                            label: f.label,
                            checked: value === 'true',
                            style: o.styles.checkbox,
                            ...changeProp(() => onChange(value === 'true' ? '' : 'true')),
                        }),
                        ...(f.required ? [text({ style: o.styles.required, children: ['*'] })] : []),
                    ],
                }),
                ...errorNode(f, o),
            ],
        });
    }
    // 2. Single choice.
    if (f.type === 'single_choice') {
        return column({
            style: o.styles.field.container,
            children: [radioGroup(f, o), ...errorNode(f, o)],
        });
    }
    const placeholder = f.type === 'date' && !f.placeholder ? DATE_PLACEHOLDER : f.placeholder || f.label;
    const icon = f.show_icon ? fieldIcon(f.icon) : null;
    // With an icon the input carries extra left padding for the glyph to sit in (`.cod-has-icon`).
    // Invariant to `invalid` for the reason above — a padded input is the same input.
    const paddedStyle = o.styles.fieldInputWithIcon;
    /**
     * 🚨 A prefix needs a gutter as wide as the prefix, not as wide as an ICON.
     *
     * This used to reuse the icon's fixed 40px for both, so any prefix past ~28px ran under the
     * placeholder — and on the legacy path, under the buyer's typed value. `prefixGutter` sizes it
     * in `ch`, which the browser resolves in the field's real font; both bundles call it so the
     * prefix clears the text identically on either.
     */
    const prefixPad = prefixGutter(f.prefix_text, Boolean(icon));
    const gutter = Boolean(icon) || Boolean(f.prefix_text);
    const base = icon ? paddedStyle : inputStyle;
    // Spread REPLACES the `padding` shorthand rather than adding a longhand beside it.
    const controlStyle = prefixPad
        ? { ...base, ...inputPaddingWithGutter(o.config, prefixPad) }
        : base;
    /**
     * Overlay the icon on the input's left padding, per `.cod-input-wrap` / `.cod-icon`.
     *
     * The first version rendered `row([icon, input])`. On a live store that put the glyph OUTSIDE the
     * field frame and shrank the input to its content width — the icon has to overlay the input, not
     * share a row with it.
     */
    /**
     * Our OWN placeholder, overlaid on the input while it is empty.
     *
     * The component's `placeholder` prop rendered nothing visible on a live store, and
     * `::placeholder` is unreachable from a style object — so the Designer's placeholder text and
     * colour could not appear at all. Rendering it ourselves is deterministic, matches the preview
     * exactly, and the admin walker reproduces it from the same tree.
     *
     * `placeholder: ''` is passed to the control so a native one can never double up with it.
     */
    /**
     * `prefix_text` — merchant-configured static text pinned inside the input (`.cod-prefix`), e.g.
     * a dial code. **Dropped entirely in the first port**: the Designer offers the setting and the
     * storefront ignored it.
     *
     * The legacy `read()` prepended it to the submitted value (`form.ts:170`), so `collect()` does
     * the same rather than the buyer's typed value losing it.
     */
    const prefixOverlay = () => f.prefix_text
        ? [
            text({
                // Drawn at the START of the gutter — after the icon when there is one, which the
                // fixed `left: 12px` in the style object could not express (it put the prefix on top
                // of the glyph whenever a field had both).
                style: { ...o.styles.prefix, left: prefixLeft(Boolean(icon)) },
                children: [f.prefix_text],
            }),
        ]
        : [];
    /**
     * Placeholder text inside the input — our own node.
     *
     * The host renders `field`'s label BELOW the control and ignores its `placeholder` prop, so
     * neither can put text inside it. This is rendered while the field is empty and turns transparent
     * on focus via `inputWrapCss`, so typing never needs a rebuild.
     */
    const placeholderOverlay = (iconTop) => value
        ? []
        : [
            text({
                style: {
                    ...o.styles.placeholder,
                    ...(gutter ? o.styles.placeholderWithIcon : {}),
                    // Past the prefix, not merely past an icon's width — this is the overlap in the
                    // screenshot: "prefix" drawn from 12px, the placeholder pinned at 40px.
                    ...(prefixPad ? { left: prefixPad } : {}),
                    ...(iconTop ? o.styles.placeholderTop : {}),
                },
                children: [placeholder],
            }),
        ];
    const StyledWrap = styled(box) `
    ${inputWrapCss(o.config)}
  `;
    const wrap = (control, iconTop = false) => StyledWrap({
        style: o.styles.inputWrap,
        children: [
            ...(icon
                ? [
                    box({
                        style: iconTop
                            ? { ...o.styles.inputIcon, ...o.styles.inputIconTop }
                            : o.styles.inputIcon,
                        children: [icon],
                    }),
                ]
                : []),
            control,
            ...prefixOverlay(),
            ...placeholderOverlay(iconTop),
        ],
    });
    // 3. Date — a tappable control plus the calendar it opens, both drawn by us.
    //
    // 🚨 It sits BEFORE the coverage branch on purpose: a `date` is never a coverage level, and a
    // future config that named one would be better off rendering a broken cascade than a date field
    // silently losing its picker.
    //
    // No `wrap()` here — that helper adds the placeholder and prefix overlays, and the picker draws
    // its own placeholder inside the control. `prefix_text` is deliberately ignored: a dial code in
    // front of a calendar date is not a thing, and the Designer only offers the control on text-like
    // fields anyway.
    if (f.type === 'date') {
        return column({
            style: o.styles.field.container,
            children: [
                labelNode(f, o),
                ...dateControlNodes(f, o, { icon, placeholder }),
                ...errorNode(f, o),
            ],
        });
    }
    // 4. Coverage select, and 5. dropdown — same component, different option source.
    if (f.type === 'dropdown' || o.coverageOptions) {
        const options = (o.coverageOptions ?? f.options ?? []).map((opt) => ({
            label: opt,
            value: opt,
        }));
        // A variable, never an inline literal: `container` is honoured by the adapter but absent from
        // `NubeComponentSelectProps['style']`, and an object literal would be excess-property checked.
        // See `SelectStyle`.
        const selectStyle = {
            container: o.styles.nativeContainer,
            label: o.styles.nativeLabelHidden,
            select: controlStyle,
        };
        return column({
            style: o.styles.field.container,
            children: [
                labelNode(f, o),
                select({
                    name: f.key,
                    // Still passed for accessibility, but hidden — see `nativeLabelHidden`.
                    label: f.label,
                    value,
                    disabled: o.disabled ?? false,
                    // A placeholder row so an untouched select reads as a prompt, not as its first option
                    // silently selected — the original seeded exactly this (`form.ts:146`, `:122`).
                    options: [{ label: placeholder, value: '' }, ...options],
                    style: selectStyle,
                    ...changeProp((e) => onChange(String(e.value ?? ''))),
                }),
                ...errorNode(f, o),
            ],
        });
    }
    // 6. Textarea.
    //
    // ⚠️ `textarea` has **no `placeholder` prop** — only `name`/`label`/`value`/`maxLength`/`row`/
    // `mask`. The legacy `<textarea>` carried one (`form.ts:131`), so a merchant who wrote a
    // placeholder for a textarea loses it here. The label above still identifies the field, and
    // `maxLength` is honoured natively, which the original enforced only at submit.
    if (f.type === 'textarea') {
        // The full control skin, shared by both channels so they cannot drift — see `codTextareaCss`
        // for why this one component needs the `styled()` channel at all.
        const textareaStyle = { ...controlStyle, ...o.styles.textarea };
        const StyledTextarea = styled(textarea) `
      ${codTextareaCss(o.config, textareaStyle)}
    `;
        return column({
            style: o.styles.field.container,
            children: [
                labelNode(f, o),
                wrap(StyledTextarea({
                    name: f.key,
                    label: f.label,
                    ...(f.max_length > 0 ? { maxLength: f.max_length } : {}),
                    style: {
                        // 🚨 `nativeContainer`, exactly like the single-line field below: `width: 100%`,
                        // because without it the container is a flex item sized by its content and a
                        // `<textarea>` has an intrinsic width (~20 cols) — the note field rendered narrower
                        // than every input beside it — plus the reset for the theme's `form-group` margin.
                        container: o.styles.nativeContainer,
                        label: o.styles.nativeLabelHidden,
                        // `.cod-textarea` adds a min-height and vertical resize on top of the input look.
                        // Kept even though the host ignores it for this component — it is what the published
                        // type asks for, and what comes back to life if the adapter is fixed.
                        input: textareaStyle,
                    },
                    ...changeProp((e) => onChange(String(e.value ?? ''))),
                    ...(o.handlers?.onFieldBlur ? { onBlur: () => o.handlers?.onFieldBlur?.(f.key) } : {}),
                }), true),
                ...errorNode(f, o),
            ],
        });
    }
    // 7. Single-line input: text / tel / email / number.
    //
    // `date` never reaches here — it returned above as a picker. `FormFieldInputType` still has no
    // `date`, so there is no native control to fall back to on either path (`MIGRATION.md` §12.0).
    //
    // `styled()` rather than plain `field`, so `::placeholder` and `:focus` — neither expressible in
    // a style object — reach the input. See `codInputCss`.
    const StyledField = styled(field) `
    ${codInputCss(o.config)}
  `;
    const input = StyledField({
        name: f.key,
        // Passed for accessibility, hidden visually — the host renders it BELOW the input.
        label: f.label,
        // NO `value`: uncontrolled, so typing never needs a rebuild. The visible placeholder is the
        // overlay above; this stays empty so the two can never double up.
        placeholder: '',
        // 🚨 **NO `autoFocus`** — the second half of the same bug as `inputStyle` above, and the half
        // that survived the first fix. `autoFocus` was set on the FIRST invalid field after a failed
        // submit, so that one field's props changed, its `__internalId` changed, the host remounted it
        // and the buyer's text vanished — while every OTHER invalid field kept its text, which is
        // exactly what the merchant photographed.
        //
        // It cannot be made conditional, either. Whatever field carries `autoFocus` LOSES it on the
        // next build — the buyer types, the error clears, `firstErrorKey` moves on — and losing a prop
        // is the same prop change in reverse: the character they just typed goes with it. There is no
        // state in which this prop is safe on an uncontrolled input.
        //
        // What it cost: on a failed submit the SDK no longer brings the first error into view. That is
        // the platform gap `BuildOptions.focusKey` already documents for the thank-you panel — a worker
        // cannot measure a component's offset, and `autoFocus` was the one borrowed substitute. The
        // inline message under each field still renders. Better a form the buyer must scroll than one
        // that eats what they typed.
        // `allow_only_numbers`: the legacy set `inputmode=numeric` and stripped non-digits on every
        // keystroke (`form.ts:161-167`). A mask of the field's max length is the SDK equivalent —
        // `9` accepts a digit and nothing else — so the buyer physically cannot type a letter.
        ...(f.allow_only_numbers ? { mask: '9'.repeat(f.max_length > 0 ? f.max_length : 20) } : {}),
        style: {
            // 🚨 `nativeContainer`, not `{ width: '100%' }`: the host gives this wrapper the THEME's
            // `form-group` class, whose `margin-bottom` is what put a band of space between every input
            // and the error under it. See its header in `theme.ts`.
            container: o.styles.nativeContainer,
            label: o.styles.nativeLabelHidden,
            input: controlStyle,
        },
        ...changeProp((e) => onChange(String(e.value ?? ''))),
        ...(o.handlers?.onFieldBlur ? { onBlur: () => o.handlers?.onFieldBlur?.(f.key) } : {}),
    });
    return column({
        style: o.styles.field.container,
        children: [labelNode(f, o), wrap(input), ...errorNode(f, o)],
    });
}
//# sourceMappingURL=fields.js.map